/**
 * Phase 19 — the rule surfaces in the operator's words: what a rule judges,
 * each action and what it does to the day, and the condition a new rule
 * starts from. One table so the cards, the badges and the tutorial say the
 * same thing. Phase 22: Exclude on every surface (lists too) — the default
 * rule "Not enough data" replaces the old volume floors.
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  DROP_GEN_GATEWAY_RULE_ACTIONS,
  DROP_GEN_HOUR_RULE_ACTIONS,
  DROP_GEN_LIST_RULE_ACTIONS,
  DROP_GEN_TEXT_RULE_ACTIONS,
  type DropGenRuleOp,
} from '@trafficpulse/shared';
import {
  Archive,
  BadgeCheck,
  ChevronsUp,
  Copy,
  FolderX,
  LifeBuoy,
  LogOut,
  TimerOff,
  ZapOff,
  type LucideIcon,
} from 'lucide-react';

import { RULE_ACTION_META } from '@/features/flow-optimizer/tabs/exclude/rule-action-meta';

/** Exclude looks exactly like the Flow Optimizer's (user, 12/10/2026: same icon, same red — or clients read it as a different thing). */
export const EXCLUDE_LOOK = { Icon: RULE_ACTION_META.exclude!.Icon, tone: RULE_ACTION_META.exclude!.tone };

export type RuleSurface = 'hours' | 'texts' | 'gateways' | 'lists' | 'routeTest';

export interface RuleActionCopy {
  label: string;
  /** What the action does to the day, in one sentence. */
  meaning: string;
  /** The short badge a result row shows when it fired. */
  badge: string;
  icon: LucideIcon;
  /** Red = takes away (Exclude too, as in the Flow Optimizer), amber = holds back, green = favours — the picker, badges and the hour chart agree. */
  tone: RuleActionTone;
}

export type RuleActionTone = 'destructive' | 'warning' | 'success';

export const RULE_TONE_COLOR: Record<RuleActionTone, string> = {
  destructive: 'hsl(var(--destructive))',
  warning: 'hsl(var(--warning))',
  success: 'hsl(var(--success))',
};

interface SurfaceCopy {
  /** "hour", "text", "gateway" — what one rule judges. */
  item: string;
  intro: string;
  actions: readonly string[];
  starter: { metric: string; op: DropGenRuleOp; value: number };
  /** What Exclude does to this surface's item, in one sentence. */
  excludes: string;
  /** The Tag Rules tags its rules may test (Phase 25: every surface) — the picker's section, the page they live on. */
  tags: { group: string; page: string };
}

export const RULE_SURFACES: Record<RuleSurface, SurfaceCopy> = {
  hours: {
    item: 'hour',
    intro: "Rules judge each hour of the day on this campaign's own results, over the rule's own date range — or on the hour tags it carries (Tag Rules › Hours). They act while the Hour Optimizer is on.",
    actions: DROP_GEN_HOUR_RULE_ACTIONS,
    starter: { metric: 'roi', op: '<', value: 0 },
    excludes: 'the hour is out of the ranking — no extra drop, no other rule judges it; the daily test drop may still try it, and its own slot drops stay.',
    tags: { group: 'Hour tag', page: 'Tag Rules › Hours' },
  },
  texts: {
    item: 'text',
    intro: "Rules judge each text — and each prompt group on its texts together — on this campaign group's results, over the rule's own date range, or on the text tags it carries (Tag Rules › Texts; a prompt group carries a tag when any of its texts does).",
    actions: DROP_GEN_TEXT_RULE_ACTIONS,
    starter: { metric: 'roi', op: '<', value: 0 },
    excludes: 'the text (or prompt group) is out of the ranking — never reused, no other rule judges it; never counted as bad.',
    tags: { group: 'Text tag', page: 'Tag Rules › Texts' },
  },
  gateways: {
    item: 'gateway',
    intro: "Rules judge each gateway of the pool on its own results on this campaign group's tracker, over the rule's own date range, or on the gateway tags it carries (Tag Rules › Gateways). The gateway verdict (Verdicts tab) is separate: it judges today's sends.",
    actions: DROP_GEN_GATEWAY_RULE_ACTIONS,
    starter: { metric: 'roi', op: '<', value: 0 },
    excludes: 'the gateway is out of the ranking — after the ranked ones in formula order, the average share in formula weights; no other rule judges it.',
    tags: { group: 'Gateway tag', page: 'Tag Rules › Gateways' },
  },
  lists: {
    item: 'list',
    intro: "Rules judge each list of the group on its own results on this campaign group's tracker, over the rule's own date range, or on the list tags it carries (Tag Rules › Lists, static tags, New / Unused).",
    actions: DROP_GEN_LIST_RULE_ACTIONS,
    starter: { metric: 'uniqueVisits', op: '<', value: 100 },
    excludes: 'the list is out of the ranking — it keeps its place after the ranked lists (biggest first); never counted as bad.',
    tags: { group: 'List tag', page: 'Tag Rules › Lists' },
  },
  routeTest: {
    item: 'gateway',
    intro: "Rules judge each gateway on this route test’s own drops from today. No ‘Use for the rest of today’ rule: the best-scored gateway wins. Add one to require a bar first (for example Delivered rate above 80 %) — then only the gateways it holds for can win. The winner and its best sender go onto the other containers’ unsent drops.",
    actions: ['exclude', 'use_rest_of_day', 'leave_pool', 'last_resort'],
    starter: { metric: 'roi', op: '>', value: 0 },
    excludes: 'the gateway is out of the ranking.',
    tags: { group: 'Gateway tag', page: 'Tag Rules › Gateways' },
  },
};

export const RULE_ACTIONS: Record<string, RuleActionCopy> = {
  exclude: { label: 'Exclude', badge: 'excluded', icon: EXCLUDE_LOOK.Icon, tone: 'destructive', meaning: 'out of the ranking — not judged yet: no other rule acts on it, never counted as bad.' },
  reduce_slots: { label: 'Reduce slots', badge: 'slots reduced', icon: TimerOff, tone: 'warning', meaning: 'remove the latest eligible slots in this hour, keeping the configured minimum; sent and in-flight drops stay.' },
  no_extra: { label: 'No extra drop', badge: 'no extra', icon: ZapOff, tone: 'warning', meaning: 'the hour never gets an extra drop or a probe — its own slot drops stay.' },
  block: { label: 'Block the hour', badge: 'blocked', icon: TimerOff, tone: 'destructive', meaning: 'no drop in that hour today — its slot drops are skipped, with the rule named.' },
  extra_first: { label: 'Extras here first', badge: 'extras first', icon: ChevronsUp, tone: 'success', meaning: 'the hour is tried first when the optimizer adds an extra drop.' },
  retire: { label: 'Never reuse the text', badge: 'retired', icon: Archive, tone: 'destructive', meaning: 'the text leaves the reuse pool — drafts never get it again.' },
  proven: { label: 'Proven text', badge: 'proven', icon: BadgeCheck, tone: 'success', meaning: 'once a Proven rule is on, only the texts it fires on may be reused.' },
  retire_group: {
    label: 'Retire the prompt group',
    badge: 'group retired',
    icon: FolderX,
    tone: 'destructive',
    meaning: 'judged on the group’s texts together: no fresh AI texts from that prompt group (if every group is retired, they all keep giving texts).',
  },
  leave_pool: { label: 'Leave the pool', badge: 'out of the pool', icon: LogOut, tone: 'destructive', meaning: 'the gateway sends nothing today (if every gateway is out, the pool stays as it is).' },
  last_resort: { label: 'Last resort', badge: 'last resort', icon: LifeBuoy, tone: 'warning', meaning: 'the gateway is used only when every other gateway is full or out.' },
  use_rest_of_day: {
    label: 'Use for the rest of today',
    badge: 'used today',
    icon: Copy,
    tone: 'success',
    meaning: 'only the gateways this fires on can win (without such a rule the best-scored gateway wins); the best of them and its best sender are copied onto the other containers’ unsent drops.',
  },
};

export const RULES_HONESTY =
  'A rule acts only on what the data proves: with a value missing it does nothing — missing data never counts as bad. Exclude is the exception: an item it cannot judge stays out of the ranking ("not judged yet"), and an excluded item is judged by no other rule.';

/** The words under a rule's action — Exclude says what it does to this surface's item. */
export const actionMeaning = (surface: RuleSurface, action: string) =>
  action === 'exclude' ? RULE_SURFACES[surface].excludes : (RULE_ACTIONS[action]?.meaning ?? '');
